import MainMenu from '@/views/MainMenu.vue'
import type { RouteRecordRaw } from 'vue-router'

const gameRoute: RouteRecordRaw = {
  path: '/game',
  name: 'game',
  component: MainMenu,
  meta: {
    menuTitle: 'Game',
  },
  children: [
    {
      path: 'classes-and-jobs',
      name: 'classes-and-jobs',
      component: () => import('@/views/game/ClassesJobs/ClassesJobsList.vue'),
      meta: {
        menuTitle: 'Classes & Jobs',
        subTitle: 'Each class has their own story to tell',
        banners: ['explore-maplestory', 'quick-start', 'world-map'],
        seo: {
          description:
            'Each character and class has their own questline and tale to tell - try them all to get the full story!',
          keywords:
            'maplestory classes, maplestory characters, maplestory jobs, maplestory class guide, maplestory best class, maplestory easiest class, gms class guide',
        },
        gaPageType: 'heroes - all',
        // the modern half of the Classes & Jobs pair; the classic half declares the mirror
        worldToggle: {
          modernRoute: { name: 'classes-and-jobs' },
          classicRoute: { name: 'classes-and-jobs-classic' },
        },
      },
    },
    {
      path: 'classes-and-jobs/:classID/:className/:seo?',
      name: 'classes-and-jobs-detail',
      component: () => import('@/views/game/ClassesJobs/ClassesJobsDetail.vue'),
      meta: {
        menuTitle: 'Classes & Jobs',
        hideOnMenu: true,
        fullPage: true,
        banners: ['explore-maplestory', 'quick-start', 'world-map'],
        seo: {
          description:
            'Each character and class has their own questline and tale to tell - try them all to get the full story!',
        },
        gaPageType: 'heroes - detail',
        noscroll: true,
      },
    },
    {
      path: '6th-job-skills',
      name: 'sixth-job-skills',
      component: () => import('@/views/game/6thJobSkills/6thJobSkills.vue'),
      meta: {
        menuTitle: '6th Job Skills',
        subTitle: 'unlock your maximum potential with 6th job',
        banners: ['classes-and-jobs', 'news-index', 'maple-guides'],
        seo: {
          title: '6th Job',
          description: "Unlock your character's maximum potential with powerful 6th Job skills!",
          keywords:
            'maplestory 6th job, gms 6th job skills, maplestory hexa matrix, hexa matrix, 6th job advancement, 6th job guide, 6th job skill videos',
        },
        gaPageType: 'heroes - 6th job',
      },
    },
    {
      path: '',
      name: 'em-mw-qs',
      component: () => import('@/views/game/ExploreMaplestoryMenu.vue'),
      meta: {
        gaPageType: 'introduction',
      },
      children: [
        {
          path: 'explore-maplestory',
          name: 'explore-maplestory',
          component: () => import('@/views/game/ExploreMaplestory.vue'),
          meta: {
            menuTitle: 'Explore MapleStory',
            subTitle: "explore maple world's rich history",
            banners: ['quick-start', 'world-map', 'classes-and-jobs'],
            seo: {
              title: 'Explore',
              description: 'Explore the stories, bosses, and heroes of Maple World and beyond!',
              keywords:
                'maplestory overview, maple world overview, history of maple world, maplestory explorers, maplestory black mage, maplestory kaling, maplestory seren',
            },
            worldToggle: {
              modernRoute: { name: 'explore-maplestory' },
              classicRoute: { name: 'explore-maplestory-classic' },
            },
          },
        },
        {
          path: 'world-map/:continent?',
          name: 'world-map',
          component: () => import('@/views/game/WorldMap.vue'),
          meta: {
            menuTitle: 'World Map',
            subTitle: 'Immerse yourself in diverse landscapes',
            banners: ['explore-maplestory', 'quick-start', 'classes-and-jobs'],
            seo: {
              description: 'From floating cities to giant submarines, there are so many diverse landscapes to explore!',
              keywords:
                'maple world map, gms world map, grandis map, arcane river map, gms maple world, maplestory maps, maplestory continents',
            },
            // the modern half of the World Map pair; the classic half declares the mirror
            worldToggle: {
              modernRoute: { name: 'world-map' },
              classicRoute: { name: 'world-map-classic' },
            },
          },
        },
        {
          path: 'quick-start',
          name: 'quick-start',
          component: () => import('@/views/game/QuickStart.vue'),
          meta: {
            menuTitle: 'Quick Start',
            subTitle: 'welcome to maplestory',
            banners: ['explore-maplestory', 'world-map', 'classes-and-jobs'],
            seo: {
              description: 'New to MapleStory? Check out this guide to get you started!',
              keywords:
                'how to play maple, how to play maplestory, maplestory beginner guide, getting started maplestory, maplestory UI explained, maplestory UI guide, how to start maplestory',
            },
          },
        },
      ],
    },
    {
      // Classic World keeps its own shell: a two-tab nav plus the world toggle. Unlike the
      // modern shell above it owns a URL segment, so both Classic pages live under
      // /game/mscw/ - the bare segment lands on the first tab.
      path: 'mscw',
      name: 'em-classic',
      component: () => import('@/views/game/ExploreMaplestoryClassicMenu.vue'),
      redirect: { name: 'explore-maplestory-classic' },
      meta: {
        gaPageType: 'introduction',
        worldToggle: {
          modernRoute: { name: 'explore-maplestory' },
          classicRoute: { name: 'explore-maplestory-classic' },
        },
      },
      children: [
        {
          // `-classic` stays in the name only: route names are global and the modern page
          // already owns `explore-maplestory`. Every link to this page is by name.
          path: 'explore-maplestory',
          name: 'explore-maplestory-classic',
          component: () => import('@/views/game/ExploreMaplestoryClassic.vue'),
          meta: {
            menuTitle: 'Explore MapleStory',
            subTitle: "explore maple world's rich history",
            hideOnMenu: true,
            // The Classic artboards draw no suggestion banners (their `bottom banners`
            // layer is hidden), so this is an empty list rather than a removed key:
            // PageBanners falls back to a default trio when `banners` is undefined.
            banners: ['classes-and-jobs-classic', 'maple-guides-classic', 'news-classic'],
            seo: {
              title: 'Explore',
              description: 'Explore exclusive content, jobs, and social features of MapleStory Classic World!',
              keywords:
                'maplestory classic world, gms classic world, maplestory classic, classic world jobs, classic world map, forgotten hollow',
            },
          },
        },
        {
          // Same shape as the modern World Map's `world-map/:continent?`: the selected island
          // is part of the URL, so /game/mscw/world-map/victoria-island and
          // /game/mscw/world-map/maple-island are each linkable and the back button steps
          // between them. Optional, because every existing link to this route is by name with
          // no params - the page normalises a missing or unknown slug to the first island.
          path: 'world-map/:continent?',
          name: 'world-map-classic',
          component: () => import('@/views/game/WorldMapClassic.vue'),
          meta: {
            menuTitle: 'World Map',
            // both islands are live, so this can no longer name only Victoria
            subTitle: 'explore maple island and victoria island',
            hideOnMenu: true,
            // The Classic artboards draw no suggestion banners (their `bottom banners`
            // layer is hidden), so this is an empty list rather than a removed key:
            // PageBanners falls back to a default trio when `banners` is undefined.
            banners: ['classes-and-jobs-classic', 'maple-guides-classic', 'news-classic'],
            seo: {
              title: 'World Map',
              description: 'Start your adventure on Maple Island before embarking on your journey to Victoria Island!',
              keywords:
                'classic world map, maplestory classic world map, victoria island map, maple island, henesys, perion, ellinia, kerning city, forgotten hollow',
            },
            // this route is not under `em-classic`'s own worldToggle by accident: the pair
            // it belongs to is the World Map pair, so it overrides the inherited one
            worldToggle: {
              modernRoute: { name: 'world-map' },
              classicRoute: { name: 'world-map-classic' },
            },
          },
        },
      ],
    },
    {
      // The Classic Classes & Jobs page. Under the /game/mscw/ segment like its two siblings,
      // but NOT a child of `em-classic`: that shell draws the Explore / World Map tab bar and
      // this artboard has none - just as the modern `classes-and-jobs` above sits outside the
      // modern `em-mw-qs` shell. Same `-classic` naming: the modern page owns the bare name,
      // and every link to either page is by name.
      path: 'mscw/classes-and-jobs',
      name: 'classes-and-jobs-classic',
      component: () => import('@/views/game/ClassesJobsClassic/ClassesJobsListClassic.vue'),
      meta: {
        menuTitle: 'Classes & Jobs',
        subTitle: 'Each class has their own story to tell',
        hideOnMenu: true,
        banners: ['explore-maplestory-classic', 'world-map-classic', 'news-classic'],
        seo: {
          title: 'Classes & Jobs',
          description:
            'Each character follows their own path in MapleStory Classic World - try them all to get the full story!',
          keywords:
            'maplestory classic world classes, maplestory classic world characters, maplestory classic world jobs, maplestory classic world class guide, maplestory classic world best class, maplestory classic world easiest class, classic world class guide',
        },
        gaPageType: 'classic heroes - all',
        worldToggle: {
          modernRoute: { name: 'classes-and-jobs' },
          classicRoute: { name: 'classes-and-jobs-classic' },
        },
      },
    },
    {
      // Mirror of the modern detail route above, on the same CDN data for now.
      path: 'mscw/classes-and-jobs/:classID/:className/:seo?',
      name: 'classes-and-jobs-detail-classic',
      component: () => import('@/views/game/ClassesJobsClassic/ClassesJobsDetailClassic.vue'),
      meta: {
        menuTitle: 'Classes & Jobs',
        hideOnMenu: true,
        fullPage: true,
        banners: ['explore-maplestory-classic', 'world-map-classic', 'news-classic'],
        seo: {
          title: 'Classes & Jobs',
          description:
            'Each character follows their own path in MapleStory Classic World - try them all to get the full story!',
          keywords:
            'maplestory classic world classes, maplestory classic world characters, maplestory classic world jobs, maplestory classic world class guide, maplestory classic world best class, maplestory classic world easiest class, classic world class guide',
        },
        gaPageType: 'classic heroes - detail',
        noscroll: true,
      },
    },
    {
      // The Classic Maple Guides page, on Classic World's own guide feed (see
      // store/MapleGuideClassic). Under /game/mscw/ like its siblings but NOT a child of
      // `em-classic`: that shell draws the Explore / World Map tab bar, and this page has no
      // tab bar of its own.
      path: 'mscw/maple-guides',
      name: 'maple-guides-classic',
      component: () => import('@/views/game/MapleGuideClassic/index.vue'),
      // No empty-path child, so the bare URL and every by-name link to this record (the world
      // toggle, the nav menu) match this record last and take the redirect to `/all`.
      redirect: { name: 'maple-guide-list-classic', params: { category: 'all' } },
      meta: {
        menuTitle: 'Maple Guides',
        subTitle: 'quick courses in gameplay elements',
        hideOnMenu: true,
        banners: ['explore-maplestory-classic', 'world-map-classic', 'news-classic'],
        seo: {
          title: 'Maple Guides',
          description: 'Learn more about MapleStory Classic World with these quick guides!',
          keywords:
            'maplestory classic world guide, classic world maple guides, maplestory classic world how to play, maplestory classic world tips, maplestory classic world walkthrough',
        },
        gaPageType: 'classic information',
        worldToggle: {
          modernRoute: { name: 'maple-guides' },
          classicRoute: { name: 'maple-guides-classic' },
        },
      },
      children: [
        {
          // The segment only exists so the URL has the modern page's shape
          // (`/maple-guides/all`); Classic publishes one flat list with no category filter, so
          // `all` is the whole accepted set for now.
          path: ':category(all)',
          name: 'maple-guide-list-classic',
          component: () => import('@/views/game/MapleGuideClassic/GuideListClassic.vue'),
          meta: {
            seo: {
              title: 'Maple Guides',
              description: 'Learn more about MapleStory Classic World with these quick guides!',
              keywords:
                'maplestory classic world guide, classic world maple guides, maplestory classic world how to play, maplestory classic world tips, maplestory classic world walkthrough',
            },
          },
        },
        {
          path: ':category(all)/:id(\\d+)/:seoTitle?',
          name: 'maple-guide-detail-classic',
          component: () => import('@/views/game/MapleGuideClassic/GuideDetailClassic.vue'),
        },
        {
          // Anything else in the segment (`/videos`, a typo) is mapped onto `all`, as
          // FilterRouteBox does for an unknown category on the modern page. vue-router ranks a
          // param with a custom regexp above a bare one, so `/all` itself never lands here.
          path: ':category',
          redirect: { name: 'maple-guide-list-classic', params: { category: 'all' } },
        },
      ],
    },
    {
      // The Classic pages were shared internally under /game/*-classic before they moved to
      // /game/mscw/. Nothing links here any more - these only keep those links alive.
      path: 'explore-maplestory-classic',
      redirect: { name: 'explore-maplestory-classic' },
    },
    {
      path: 'world-map-classic/:continent?',
      redirect: (to) => ({
        name: 'world-map-classic',
        params: { continent: to.params.continent },
        query: to.query,
      }),
    },
    {
      path: 'maple-guides/:category?',
      name: 'maple-guides',
      component: () => import('@/views/game/MapleGuide/index.vue'),
      redirect: { name: 'maple-guide-list' },
      meta: {
        menuTitle: 'Maple Guides',
        subTitle: 'quick courses in gameplay elements',
        banners: ['news-index', 'classes-and-jobs', 'player-ranking'],
        seo: {
          title: 'Guides',
          description: 'Check out the Maple Guides for tips and quick courses in different elements of gameplay!',
        },
        gaPageType: 'information',
        // the modern half of the Maple Guides pair; declared on the parent so the list and
        // the detail below both inherit it
        worldToggle: {
          modernRoute: { name: 'maple-guides' },
          classicRoute: { name: 'maple-guides-classic' },
        },
      },
      children: [
        {
          path: '',
          name: 'maple-guide-list',
          component: () => import('@/views/game/MapleGuide/GuideList.vue'),
          meta: {
            seo: {
              keywords:
                'maplestory guide, maplestory glossary, returning mapler guide, gms maple guides, maplestory link skills, maplestory 5th job, maplestory v matrix',
            },
          },
        },
        {
          path: ':id(\\d+)/:seoTitle?',
          name: 'maple-guide-detail',
          component: () => import('@/views/game/MapleGuide/GuideDetail.vue'),
        },
      ],
    },
    {
      path: 'random-box-rates',
      name: 'random-box-rates',
      component: () => import('@/views/news/NewsDetail.vue'),
      meta: {
        menuTitle: 'Random Box Rates',
        cdnApiPath: `/general-posts/${import.meta.env.VITE_APP_CDN_HOST === 'test.g.nexonstatic.com' ? 3982 : 5805}`,
        banners: ['news-index', 'player-ranking', 'maple-guides'],
        seo: {
          title: 'Box Rates',
          description:
            'Look up the reward rates for different types of boxes that give you a surprise reward from a master list!',
          keywords:
            'maplestory cash shop rates, gms cash shop rates, gms probability rates, gms pssb rates, maplestory gachapon reward rates, maplestory gachapon, maplestory philosophers books',
        },
        gaPageType: 'information',
      },
    },
    {
      path: '__',
      name: 'wiki',
      redirect: '//maplestorywiki.net/w/MapleStory_Wiki',
      meta: {
        menuTitle: 'Wiki',
      },
    },
  ],
}

export default gameRoute
